import { Body1Strong, Button, Checkbox } from '@fluentui/react-components';
import { CheckmarkRegular } from '@fluentui/react-icons';
import { ButtonLoading } from '@shared/components';
import { EItemScreenMode, TResolvedItemsMap, useItemScreenStore } from '@modules/items';
import { isTDocument, TDocument, TVersion } from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { useState } from 'react';
import ImpactCard from './ImpactCard';
import useImpacts from '../hooks/useImpacts';
import { useCompleteAnalysis } from '../api';
import { TTagAssociationItemType } from '@modules/tags';
import ImpactAnalysisPanel from './ImpactAnalysisPanel';
import ChangingSections from './ChangingSections';

type TProps = {
  item: TDocument | TFile;
  resolvedItems: TResolvedItemsMap;
  version: TVersion | TFileVersion;
};

const PostChangeImpactAnalysis = ({ item, resolvedItems, version }: TProps) => {
  const isDoc = isTDocument(item);

  const { setScreenMode } = useItemScreenStore();

  const changedHeadingIds = ((version as TVersion).changedSections || []).map((s) => s.headingId);
  const impacts = useImpacts(
    item,
    (version as TVersion).headings,
    resolvedItems,
    changedHeadingIds,
    EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS
  );

  const [deselectImpacts, setDeselectedImpacts] = useState<{ itemId: string; headingId?: string }[]>([]);
  const [generateComments, setGenerateComments] = useState(true);
  const completeAnalysis = useCompleteAnalysis();
  const handleCompleteAnalysis = async () => {
    await completeAnalysis.mutateAsync({
      itemId: item._id,
      itemtype: item.type,
      versionId: version.id,
      impacts: impacts
        .map((i) => ({
          itemId: i.item._id,
          itemType: i.item.type as TTagAssociationItemType,
          headingIds: i.headings
            ?.map((h) => h.id)
            ?.filter((hId) => !deselectImpacts.find((d) => d.itemId === i.item._id && d.headingId === hId)),
          generateComment: generateComments,
        }))
        .filter((i) =>
          i.headingIds ? i.headingIds.length > 0 : !deselectImpacts.some((d) => d.itemId === i.itemId && !d.headingId)
        ),
    });
    setScreenMode();
  };

  return (
    <ImpactAnalysisPanel title={`Analyze impacts of v${version.id}`}>
      <div className="flex flex-col gap-6">
        {isDoc && (
          <div>
            <p className="mb-2">Sections that have been changed in v{version.id}:</p>
            <ChangingSections
              version={version as TVersion}
              changedSectionIds={(version as TVersion).changedSections.map((s) => s.headingId)}
            />
          </div>
        )}

        {impacts.length === 0 ? (
          <p>No impacts found.</p>
        ) : (
          <>
            <div>
              <p className="mb-2">
                <Body1Strong>Step 1:</Body1Strong> Select the files or sections that could be affected and require
                review.
              </p>
              <div className="flex flex-col gap-2">
                {impacts.map((impact, idx) => (
                  <ImpactCard
                    {...impact}
                    selection={{
                      item: !deselectImpacts.find((d) => d.itemId === impact.item._id),
                      headingIds:
                        impact.headings
                          ?.filter(
                            (h) => !deselectImpacts.find((d) => d.itemId === impact.item._id && d.headingId === h.id)
                          )
                          .map((h) => h.id) || [],
                      onSelect: (itemId, headingId) =>
                        setDeselectedImpacts(
                          deselectImpacts.filter((d) => d.itemId !== itemId || d.headingId !== headingId)
                        ),
                      onDeselect: (itemId, headingId) => {
                        setDeselectedImpacts([...deselectImpacts, { itemId, headingId }]);
                      },
                    }}
                    key={idx}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2">
                <Body1Strong>Step 2:</Body1Strong> Auto-generate comments?
              </p>

              <Checkbox
                label="Generate impact-related tasks for each file or section selected above."
                checked={generateComments}
                onChange={(e) => setGenerateComments(e.target.checked)}
              />
            </div>
            <div>
              <p>
                <Body1Strong>Step 3:</Body1Strong> Mark impact analysis as completed.
              </p>
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <ButtonLoading
          appearance="primary"
          size="small"
          icon={<CheckmarkRegular />}
          onClick={handleCompleteAnalysis}
          loading={completeAnalysis.isPending}
        >
          Finish analysis
        </ButtonLoading>
        <Button size="small" onClick={() => setScreenMode()}>
          Cancel
        </Button>
      </div>
    </ImpactAnalysisPanel>
  );
};

export default PostChangeImpactAnalysis;
